// ============================================================
// GitIntro.jsx
//
// A branded, responsive course content page for the
// "Introduction to Git & GitHub" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Version control fundamentals, collaborative
//           workflow, and global configuration setup.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import GitHubIcon             from "@mui/icons-material/GitHub";
import AccountTreeIcon        from "@mui/icons-material/AccountTree";
import HistoryIcon            from "@mui/icons-material/History";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CloudQueueIcon         from "@mui/icons-material/CloudQueue";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitIntro() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<GitHubIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Version Control 101"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git & <span>GitHub Fundamentals</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Master the tools of modern collaboration. Git tracks your history, while GitHub connects you to the world’s largest community of developers.
                    </Typography>
                </Box>

                {/* 1. DEFINITIONS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<IntegrationInstructionsIcon />} label="1. The Power Duo" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountTreeIcon fontSize="small" /> Git
                                </Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 13, lineHeight: 1.6 }}>
                                    A distributed version control system. It lives on your local machine and tracks every line of code you change, allowing you to experiment safely.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.gold}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CloudQueueIcon fontSize="small" /> GitHub
                                </Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 13, lineHeight: 1.6 }}>
                                    A cloud platform for hosting Git repositories. It adds collaborative features like Pull Requests, Issue tracking, and CI/CD pipelines.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. CORE CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="2. Key Terminology" />
                    <Grid container spacing={1.5}>
                        {[
                            { term: "Repository", desc: "The project folder where Git tracks changes." },
                            { term: "Commit", desc: "A permanent snapshot of your staged changes with a message." },
                            { term: "Branch", desc: "An isolated environment for developing new features." },
                            { term: "Remote", desc: "A version of your repository hosted on a server (e.g., GitHub)." },
                            { term: "Merge", desc: "Combining work from one branch back into another." },
                            { term: "Clone", desc: "Downloading a copy of a remote repo to your computer." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, borderBottom: `1px solid ${BRAND.surface}` }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 18 }} />
                                    <Typography sx={{ color: BRAND.white, fontSize: 13 }}>
                                        <strong style={{ color: BRAND.goldBright }}>{item.term}:</strong> {item.desc}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 3. INITIAL SETUP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="3. Global Configuration" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Before you start committing, you must identify yourself to Git. This information is attached to your snapshots.
                    </Typography>
                    <CodeBlock
                        title="Git CLI Setup"
                        code={`# Check current version\ngit --version\n\n# Configure user identity\ngit config --global user.name "John Doe"\ngit config --global user.email "john@example.com"\n\n# Verify settings\ngit config --list`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* 4. WHY GIT? */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HistoryIcon />} label="The Developer Safety Net" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Version Tracking", desc: "Never lose a working version of your code again." },
                            { title: "Collaboration", desc: "Work with 100+ developers on the same file simultaneously." },
                            { title: "Experimentation", desc: "Create a branch, try something wild, and delete it if it fails." },
                            { title: "Audit Trail", desc: "See exactly who changed what, when, and most importantly, why." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%', textAlign: 'center' }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, mb: 1 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Back to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Think of a <strong>Commit</strong> like a "Save Point" in a video game. Before you try a difficult boss (a complex feature), make a save point so you can always go back if things go wrong!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
